/**
 * Public job-application form — hosted at /apply, NO sign-in required.
 *
 * The company website links to (or iframes) this page as its "Apply Now" /
 * careers form. On submit it POSTs to the public intake endpoint, which creates
 * a candidate in the ATS (source=website, stage=new_lead). Intentionally
 * standalone (rendered by main.tsx before the authenticated app boots) so a
 * prospective applicant never loads the internal SPA or hits an auth wall.
 *
 * Phase 2 "The Look": token-driven + theme-aware, mobile-first (applicants use
 * phones), brand-driven accent. The consent + honeypot + FormData intake are
 * preserved exactly — the checkbox text is still what gets stored & hashed.
 */
import { useState, useEffect, FormEvent, CSSProperties } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePublicBranding } from '../lib/publicBranding';
import { Card, Field, Input, Select, Textarea, Button, Icon, Badge } from '../components/ui';

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined)
  ? `${import.meta.env.VITE_API_URL}/api/v1`
  : '/api/v1';

const ROLES = ['RN', 'LPN', 'LVN', 'CNA', 'RT', 'NP', 'PA', 'Other'];

// Applicant-facing copy for a link whose job is closed or missing.
// No dash is used as punctuation. Wording stays open to every healthcare role.
export const CLOSED_POSITION_TITLE = 'This position is no longer open';
export const CLOSED_POSITION_BODY =
  'You can still apply to our talent network. It is a general application for healthcare professionals, and a recruiter will reach out when a matching opportunity is available.';
export const CLOSED_POSITION_SUCCESS =
  'Thank you. You are in our talent network. A recruiter will review your information and reach out when a matching opportunity is available.';
export const UNKNOWN_POSITION_BODY =
  'We could not confirm whether this position is still open. If it has closed, we will save your application as general interest in our talent network.';

const JOB_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type PositionCheck =
  | { status: 'idle' }
  | { status: 'checking' }
  | { status: 'open'; title: string }
  | { status: 'closed' }
  | { status: 'unknown' };

function brandVars(primary: string | null): CSSProperties | undefined {
  return primary ? ({ ['--brand-primary' as string]: primary } as CSSProperties) : undefined;
}

export default function PublicApply() {
  const [params] = useSearchParams();
  const brand = usePublicBranding();
  const jobId = params.get('job_id') ?? '';
  const jobTitle = params.get('job_title') ?? '';
  const presetRole = params.get('role') ?? '';
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [resumeName, setResumeName] = useState('');
  const [credentialName, setCredentialName] = useState('');
  // Canonical consent text comes from the backend so what the applicant sees
  // is byte-for-byte what gets stored (and hashed) with their consent record.
  const [consentText, setConsentText] = useState('');
  const [position, setPosition] = useState<PositionCheck>(jobId ? { status: 'checking' } : { status: 'idle' });
  const [savedAsGeneral, setSavedAsGeneral] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/public/privacy-policy`)
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => { if (b?.consent_text) setConsentText(b.consent_text as string); })
      .catch(() => { /* fallback copy below still requires the checkbox */ });
  }, []);

  // The public careers endpoint returns the job only while it is open, and
  // 404s when it is closed or missing. Until that answer arrives, do not show
  // the title from the link. A title in the query string can belong to a job
  // that has already closed.
  useEffect(() => {
    if (!jobId) {
      setPosition({ status: 'idle' });
      return;
    }
    if (!JOB_ID_RE.test(jobId)) {
      setPosition({ status: 'closed' });
      return;
    }
    let cancelled = false;
    setPosition({ status: 'checking' });
    fetch(`${API_BASE}/careers/jobs/${encodeURIComponent(jobId)}`)
      .then(async (r) => {
        if (cancelled) return;
        if (r.status === 404) {
          setPosition({ status: 'closed' });
          return;
        }
        if (!r.ok) {
          setPosition({ status: 'unknown' });
          return;
        }
        const body = await r.json().catch(() => null) as { job?: { title?: string } } | null;
        const title = String(body?.job?.title ?? '').trim() || jobTitle.trim() || 'Open position';
        setPosition({ status: 'open', title });
      })
      .catch(() => { if (!cancelled) setPosition({ status: 'unknown' }); });
    return () => { cancelled = true; };
  }, [jobId, jobTitle]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError('');
    const form = e.currentTarget;
    const data = new FormData(form);
    if (!data.get('first_name') || !data.get('last_name')) {
      setError('Please enter your first and last name.');
      return;
    }
    if (!data.get('email') && !data.get('phone')) {
      setError('Please enter an email or phone number so we can reach you.');
      return;
    }
    if (!data.get('consent')) {
      setError('Please check the box agreeing to be contacted so we can follow up with you.');
      return;
    }
    // Every application requires a resume + a diploma/certificate.
    if (!resumeName) {
      setError('Please attach your resume.');
      return;
    }
    if (!credentialName) {
      setError('Please attach your diploma or certificate.');
      return;
    }
    // Only an open job is submitted against that job. A closed or missing
    // link is a general application, so the closed id is left off. If the
    // check itself failed, the id is still sent and the server decides.
    if (position.status === 'open' && jobId) data.set('job_id', jobId);
    else if (position.status === 'unknown' && jobId && JOB_ID_RE.test(jobId)) data.set('job_id', jobId);
    setSubmitting(true);
    try {
      const resp = await fetch(`${API_BASE}/public/apply`, { method: 'POST', body: data });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(body?.error || 'Submission failed. Please try again.');
      setSavedAsGeneral(position.status === 'closed' || body?.general_interest === true);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--sf2)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', ...brandVars(brand.primary_color) }}>
      <div style={{ width: '100%', maxWidth: 560 }}>
        <Card>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            {brand.logo_url
              ? <img src={brand.logo_url} alt={brand.app_name} style={{ height: 40, width: 'auto', borderRadius: 6 }} />
              : <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--t1)' }}>{brand.app_name}</div>}
            <div style={{ fontSize: 13, color: 'var(--t3)', marginTop: 4 }}>{brand.company_name} — Apply / Inquire</div>
            {position.status === 'open' && (
              <div style={{ marginTop: 12 }}>
                <Badge tone="good" size="lg">Applying for: {position.title}</Badge>
              </div>
            )}
            {position.status === 'idle' && jobTitle && (
              <div style={{ marginTop: 12 }}>
                <Badge tone="good" size="lg">Applying for: {jobTitle}</Badge>
              </div>
            )}
            {position.status === 'closed' && (
              <div role="status" style={noticeBox}>
                <div style={{ fontWeight: 800, color: 'var(--t1)', marginBottom: 6 }}>{CLOSED_POSITION_TITLE}</div>
                <div>{CLOSED_POSITION_BODY}</div>
              </div>
            )}
            {position.status === 'unknown' && (
              <div role="status" style={noticeBox}>{UNKNOWN_POSITION_BODY}</div>
            )}
            {position.status === 'checking' && (
              <div role="status" style={{ fontSize: 13, color: 'var(--t3)', marginTop: 12 }}>
                Checking whether this position is still open.
              </div>
            )}
          </div>

          {done ? (
            <div style={{ textAlign: 'center', padding: '20px 8px' }}>
              <div className="ui-empty__icon" style={{ margin: '0 auto 14px', background: 'var(--good-tint)', color: 'var(--good-fg)' }}>
                <Icon name="check-circle" size={28} />
              </div>
              <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 800, color: 'var(--t1)' }}>Application received</h2>
              <p style={{ color: 'var(--t2)', fontSize: 14, lineHeight: 1.5 }}>
                {savedAsGeneral
                  ? CLOSED_POSITION_SUCCESS
                  : 'Thank you for your interest. A recruiter will review your information and reach out soon.'}
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit}>
              <div style={row}>
                <div style={col}><Field label="First name" required htmlFor="fa_first"><Input id="fa_first" name="first_name" required /></Field></div>
                <div style={col}><Field label="Last name" required htmlFor="fa_last"><Input id="fa_last" name="last_name" required /></Field></div>
              </div>
              <div style={row}>
                <div style={col}><Field label="Email" htmlFor="fa_email"><Input id="fa_email" name="email" type="email" placeholder="you@example.com" /></Field></div>
                <div style={col}><Field label="Phone" htmlFor="fa_phone"><Input id="fa_phone" name="phone" placeholder="(555) 555-5555" /></Field></div>
              </div>
              <div style={row}>
                <div style={col}><Field label="Role / discipline" htmlFor="fa_role">
                  <Select id="fa_role" name="role" defaultValue={ROLES.includes(presetRole) ? presetRole : ''}>
                    <option value="">— Select —</option>
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </Select>
                </Field></div>
                <div style={col}><Field label="Specialty (optional)" htmlFor="fa_spec"><Input id="fa_spec" name="specialty" placeholder="ICU, ER, Med-Surg…" /></Field></div>
              </div>
              <Field label="Anything you'd like us to know? (optional)" htmlFor="fa_msg">
                <Textarea id="fa_msg" name="message" style={{ minHeight: 80 }} placeholder="Availability, location preferences, etc." />
              </Field>
              <Field label="Resume (PDF, DOCX, or TXT)" required>
                <label style={fileBox}>
                  <input
                    name="resume" type="file" accept=".pdf,.docx,.txt" style={{ display: 'none' }}
                    onChange={(e) => setResumeName(e.target.files?.[0]?.name ?? '')}
                  />
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: resumeName ? 'var(--t1)' : 'var(--t3)' }}>
                    <Icon name="upload" size={16} /> {resumeName || 'Click to attach your resume'}
                  </span>
                </label>
              </Field>
              <Field label="Diploma or certificate (PDF, image, or DOCX)" required>
                <label style={fileBox}>
                  <input
                    name="credential" type="file"
                    accept=".pdf,.docx,.txt,.jpg,.jpeg,.png,.heic,.webp,image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => setCredentialName(e.target.files?.[0]?.name ?? '')}
                  />
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: credentialName ? 'var(--t1)' : 'var(--t3)' }}>
                    <Icon name="upload" size={16} /> {credentialName || 'Click to attach your diploma or certificate'}
                  </span>
                </label>
                <div style={{ fontSize: 12, color: 'var(--t3)', marginTop: 6 }}>
                  Your diploma, or a certificate for certificate-based roles (e.g. psych tech). A clear phone photo is fine.
                </div>
              </Field>

              {/* Honeypot — hidden from humans; bots fill it and get silently dropped. */}
              <input
                name="company" tabIndex={-1} autoComplete="off"
                style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
                aria-hidden="true"
              />

              {/* Communications consent — required; the exact text shown here is
                  what gets stored (verbatim + hashed) with the consent record. */}
              <label style={consentBox}>
                <input name="consent" type="checkbox" value="true" required style={{ marginTop: 3, flexShrink: 0 }} />
                <span>
                  {consentText || 'I agree to be contacted about job opportunities and my application by email, phone, and text message (SMS). Consent is not a condition of employment. Reply STOP to any text or use the unsubscribe link in any email to opt out.'}
                  {' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--pr)', fontWeight: 600 }}>
                    Privacy policy
                  </a>
                </span>
              </label>

              {error && (
                <div style={{ background: 'var(--danger-tint)', border: '1px solid var(--danger-border)', color: 'var(--danger-fg)', borderRadius: 'var(--r-md)', padding: '10px 12px', fontSize: 13, marginBottom: 12 }}>
                  {error}
                </div>
              )}

              <Button type="submit" block size="lg" loading={submitting} disabled={position.status === 'checking'}>
                {submitting
                  ? 'Submitting…'
                  : position.status === 'checking'
                    ? 'Checking this position'
                    : position.status === 'closed'
                      ? 'Submit general application'
                      : 'Submit application'}
              </Button>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}

const row: CSSProperties = { display: 'flex', gap: 12, flexWrap: 'wrap' };
const col: CSSProperties = { flex: '1 1 200px', minWidth: 0 };
const fileBox: CSSProperties = { display: 'block', padding: '14px 12px', border: '1.5px dashed var(--bd)', borderRadius: 'var(--r-md)', fontSize: 13, textAlign: 'center', cursor: 'pointer' };
const consentBox: CSSProperties = { display: 'flex', gap: 8, alignItems: 'flex-start', background: 'var(--sf2)', border: '1px solid var(--bd)', borderRadius: 'var(--r-md)', padding: '10px 12px', fontSize: 12, color: 'var(--t2)', lineHeight: 1.5, margin: '4px 0 14px', cursor: 'pointer' };
const noticeBox: CSSProperties = { textAlign: 'left', background: 'var(--info-tint)', border: '1px solid var(--info-border)', color: 'var(--info-fg)', borderRadius: 'var(--r-md)', padding: '12px 14px', fontSize: 14, lineHeight: 1.5, marginTop: 16 };
